import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  FiLock, FiUser, FiEye, FiEyeOff, FiShield,
  FiCheckCircle, FiAlertCircle, FiArrowRight,
} from 'react-icons/fi';
import './AdminLogin.css';

const evaluatePassword = (pw) => {
  const checks = {
    length: pw.length >= 12,
    upper: /[A-Z]/.test(pw),
    lower: /[a-z]/.test(pw),
    number: /\d/.test(pw),
    special: /[^A-Za-z0-9]/.test(pw),
  };
  const score = Object.values(checks).filter(Boolean).length;
  let level = 'Weak';
  if (score >= 5) level = 'Strong';
  else if (score >= 3) level = 'Medium';
  return { checks, score, level, isStrong: level === 'Strong' };
};

const AdminLogin = () => {
  const navigate = useNavigate();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const strength = useMemo(() => evaluatePassword(password), [password]);

  const meterColor = {
    Weak: '#ef4444',
    Medium: '#f59e0b',
    Strong: '#22c55e',
  }[strength.level];

  const meterFill = (strength.score / 5) * 100;

  const onSubmit = (e) => {
    e.preventDefault();
    setError('');
    if (!strength.isStrong) {
      setError('Password must be Strong to sign in.');
      return;
    }
    setLoading(true);
    setTimeout(() => {
      if (username === 'admin' && password === 'Admin@2026Secure#X9') {
        sessionStorage.setItem('astro_admin_auth', '1');
        sessionStorage.setItem('astro_admin_welcome', '1');
        navigate('/admin/dashboard');
      } else {
        setError('Invalid credentials. Please try again.');
        setLoading(false);
      }
    }, 700);
  };

  return (
    <div className="admin-login">
      <div className="al-stars" />
      <div className="al-grid" />
      <motion.form
        className="al-card"
        onSubmit={onSubmit}
        initial={{ opacity: 0, y: 24, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
      >
        <div className="al-brand">
          <div className="al-brand-icon"><FiShield /></div>
          <div>
            <h1 className="al-title">Admin Console</h1>
            <p className="al-subtitle">Secure access to the control plane</p>
          </div>
        </div>

        <label className="al-field">
          <span className="al-label">Admin Username</span>
          <div className="al-input-wrap">
            <FiUser className="al-input-icon" />
            <input
              type="text"
              autoComplete="username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="admin"
              required
            />
          </div>
        </label>

        <label className="al-field">
          <span className="al-label">Password</span>
          <div className="al-input-wrap">
            <FiLock className="al-input-icon" />
            <input
              type={showPassword ? 'text' : 'password'}
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter your password"
              required
            />
            <button
              type="button"
              className="al-eye"
              onClick={() => setShowPassword((s) => !s)}
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              {showPassword ? <FiEyeOff /> : <FiEye />}
            </button>
          </div>

          {password && (
            <motion.div
              className="al-strength"
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
            >
              <div className="al-meter">
                <div
                  className="al-meter-fill"
                  style={{ width: `${meterFill}%`, background: meterColor }}
                />
              </div>
              <div className="al-meter-row">
                <span className="al-meter-label" style={{ color: meterColor }}>
                  {strength.level}
                </span>
                <span className="al-meter-hint">12+ chars · Aa · 1 · #</span>
              </div>
              <ul className="al-checklist">
                {[
                  ['length', '12+ characters'],
                  ['upper', 'Uppercase letter'],
                  ['lower', 'Lowercase letter'],
                  ['number', 'Number'],
                  ['special', 'Special character'],
                ].map(([k, label]) => (
                  <li key={k} className={strength.checks[k] ? 'on' : 'off'}>
                    {strength.checks[k] ? <FiCheckCircle /> : <FiAlertCircle />}
                    {label}
                  </li>
                ))}
              </ul>
            </motion.div>
          )}
        </label>

        {error && (
          <motion.div
            className="al-error"
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
          >
            {error}
          </motion.div>
        )}

        <button
          type="submit"
          className="al-submit"
          disabled={!strength.isStrong || !username || loading}
        >
          {loading ? (
            <span className="al-spinner" />
          ) : (
            <>Sign in <FiArrowRight /></>
          )}
        </button>

        <div className="al-footer">
          <FiShield />
          <span>Protected by multi-factor security policy</span>
        </div>
      </motion.form>
    </div>
  );
};

export default AdminLogin;
